import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Grid,
  Divider,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";

import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import EventIcon from "@material-ui/icons/Event";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import WarningIcon from "@material-ui/icons/Warning";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SettingsIcon from "@material-ui/icons/Settings";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import SubscriptionModal from "../../components/SubscriptionModal";
import TransactionModal from "../../components/TransactionModal";
import RelatorioPanel from "./RelatorioPanel";
import ContasReceberPanel from "./ContasReceberPanel";
import FinanceiroConfigDialog from "./FinanceiroConfigDialog";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import moment from "moment";
import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import { useLocation } from "react-router-dom";

// ─────────────────────────────────────────────
// Reducers
// ─────────────────────────────────────────────

const invoiceReducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const incoming = action.payload;
    const newItems = [];
    incoming.forEach((item) => {
      const idx = state.findIndex((u) => u.id === item.id);
      if (idx !== -1) state[idx] = item;
      else newItems.push(item);
    });
    return [...state, ...newItems];
  }
  if (action.type === "RESET") return [];
  return state;
};

const txReducer = (state, action) => {
  if (action.type === "LOAD") {
    const incoming = action.payload;
    const next = [...state];
    incoming.forEach((item) => {
      const idx = next.findIndex((t) => t.id === item.id);
      if (idx !== -1) next[idx] = item;
      else next.push(item);
    });
    return next;
  }
  if (action.type === "UPDATE") {
    const idx = state.findIndex((t) => t.id === action.payload.id);
    if (idx !== -1) {
      const next = [...state];
      next[idx] = action.payload;
      return next;
    }
    return [action.payload, ...state];
  }
  if (action.type === "DELETE") {
    return state.filter((t) => t.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

// ─────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  summaryGrid: {
    marginBottom: theme.spacing(3),
  },
  summaryCard: {
    borderRadius: 12,
    padding: theme.spacing(2.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  summaryIcon: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  summaryLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  summaryValue: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  urgentRow: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(198,40,40,0.12)"
        : "rgba(255,235,235,0.8)",
  },
  payButton: {
    fontWeight: 700,
    fontSize: "0.75rem",
    borderRadius: 6,
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  filterField: {
    minWidth: 130,
  },
  addButton: {
    marginLeft: "auto",
  },
  categoryDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 6,
    verticalAlign: "middle",
  },
  incomeValue: { color: "#2e7d32", fontWeight: 700 },
  expenseValue: { color: "#c62828", fontWeight: 700 },
}));

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const statusConfig = {
  paid:    { label: "Pago",      style: { backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 } },
  overdue: { label: "Vencido",   style: { backgroundColor: "#ffebee", color: "#c62828", fontWeight: 600 } },
  open:    { label: "Em Aberto", style: { backgroundColor: "#fff8e1", color: "#f57f17", fontWeight: 600 } },
};

const getInvoiceStatus = (invoice) => {
  if (invoice.status === "paid") return "paid";
  const days = moment(invoice.dueDate).diff(moment().startOf("day"), "days");
  return days < 0 ? "overdue" : "open";
};

const txStatusConfig = {
  pending:   { label: "Pendente",  style: { backgroundColor: "#fff8e1", color: "#f57f17", fontWeight: 600 } },
  paid:      { label: "Pago",      style: { backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 } },
  cancelled: { label: "Cancelado", style: { backgroundColor: "#f5f5f5", color: "#9e9e9e", fontWeight: 600 } },
};

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" });

const getEffectiveTxStatus = (tx) => {
  if (tx.status !== "pending") return tx.status;
  const days = moment(tx.dueDate).diff(moment().startOf("day"), "days");
  return days < 0 ? "overdue" : "pending";
};

// ─────────────────────────────────────────────
// Panel: Faturas (existing content)
// ─────────────────────────────────────────────

const InvoicePanel = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { finding } = useCompanies();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [invoices, dispatch] = useReducer(invoiceReducer, []);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [companyDueDate, setCompanyDueDate] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!user?.companyId) return;
      try {
        const company = await finding(user.companyId);
        setCompanyDueDate(company.dueDate ? moment(company.dueDate) : null);
      } catch (err) {}
    };
    load();
  }, [user?.companyId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, []);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/invoices/all", { params: { pageNumber } });
        dispatch({ type: "LOAD_INVOICES", payload: data });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [pageNumber]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const handlePay = (invoice) => {
    setSelectedInvoice(invoice);
    setModalOpen(true);
  };

  const pendingInvoices = invoices.filter((inv) => getInvoiceStatus(inv) !== "paid");
  const overdueInvoices = invoices.filter((inv) => getInvoiceStatus(inv) === "overdue");
  const paidInvoices    = invoices.filter((inv) => getInvoiceStatus(inv) === "paid");

  const nextDue = companyDueDate;
  const daysUntilDue = nextDue ? nextDue.diff(moment().startOf("day"), "days") : null;
  const dueDateColor =
    daysUntilDue === null ? "#757575"
    : daysUntilDue < 0   ? "#c62828"
    : daysUntilDue <= 3  ? "#e65100"
    : daysUntilDue <= 7  ? "#f57f17"
    : "#2e7d32";

  return (
    <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
      <SubscriptionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="form-dialog-title"
        Invoice={selectedInvoice}
        contactId={null}
      />

      <Grid container spacing={2} className={classes.summaryGrid}>
        <Grid item xs={12} sm={4}>
          <Paper className={classes.summaryCard} elevation={0} variant="outlined">
            <div className={classes.summaryIcon} style={{ backgroundColor: `${dueDateColor}18` }}>
              <EventIcon style={{ color: dueDateColor, fontSize: 24 }} />
            </div>
            <div>
              <Typography className={classes.summaryLabel}>Licença válida até</Typography>
              <Typography className={classes.summaryValue}>
                {nextDue ? nextDue.format("DD/MM/YYYY") : "—"}
              </Typography>
              {daysUntilDue !== null && (
                <Typography variant="caption" style={{ color: dueDateColor, fontWeight: 600 }}>
                  {daysUntilDue < 0
                    ? `Vencida há ${Math.abs(daysUntilDue)} dias`
                    : daysUntilDue === 0
                    ? "Vence hoje"
                    : `Vence em ${daysUntilDue} dias`}
                </Typography>
              )}
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper className={classes.summaryCard} elevation={0} variant="outlined">
            <div className={classes.summaryIcon} style={{ backgroundColor: overdueInvoices.length > 0 ? "#ffebee" : "#f5f5f5" }}>
              <WarningIcon style={{ color: overdueInvoices.length > 0 ? "#c62828" : "#bdbdbd", fontSize: 24 }} />
            </div>
            <div>
              <Typography className={classes.summaryLabel}>Faturas pendentes</Typography>
              <Typography className={classes.summaryValue}>
                {pendingInvoices.length}
                {overdueInvoices.length > 0 && (
                  <span style={{ fontSize: "0.85rem", color: "#c62828", marginLeft: 6 }}>
                    ({overdueInvoices.length} vencida{overdueInvoices.length > 1 ? "s" : ""})
                  </span>
                )}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {pendingInvoices.length === 0 ? "Nenhuma pendência" : "Regularize para evitar bloqueio"}
              </Typography>
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper className={classes.summaryCard} elevation={0} variant="outlined">
            <div className={classes.summaryIcon} style={{ backgroundColor: "#e8f5e9" }}>
              <CheckCircleOutlineIcon style={{ color: "#2e7d32", fontSize: 24 }} />
            </div>
            <div>
              <Typography className={classes.summaryLabel}>Total pago (histórico)</Typography>
              <Typography className={classes.summaryValue}>
                {fmtBRL(paidInvoices.reduce((sum, inv) => sum + (inv.value || 0), 0))}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {paidInvoices.length} fatura{paidInvoices.length !== 1 ? "s" : ""} paga{paidInvoices.length !== 1 ? "s" : ""}
              </Typography>
            </div>
          </Paper>
        </Grid>
      </Grid>

      <Divider style={{ marginBottom: 16 }} />

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Descrição</TableCell>
            <TableCell align="center">Valor</TableCell>
            <TableCell align="center">Vencimento</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Ação</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((invoice) => {
            const status = getInvoiceStatus(invoice);
            const cfg = statusConfig[status];
            return (
              <TableRow key={invoice.id} className={status === "overdue" ? classes.urgentRow : ""}>
                <TableCell>{invoice.detail || "—"}</TableCell>
                <TableCell align="center" style={{ fontWeight: 700 }}>
                  {fmtBRL(invoice.value)}
                </TableCell>
                <TableCell align="center">{moment(invoice.dueDate).format("DD/MM/YYYY")}</TableCell>
                <TableCell align="center">
                  <Chip label={cfg.label} size="small" style={{ ...cfg.style, borderRadius: 8, height: 22, fontSize: "0.72rem" }} />
                </TableCell>
                <TableCell align="center">
                  {status !== "paid" ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disableElevation
                      className={classes.payButton}
                      onClick={() => handlePay(invoice)}
                    >
                      Pagar
                    </Button>
                  ) : (
                    <Typography variant="caption" style={{ color: "#2e7d32", fontWeight: 600 }}>
                      ✓ Pago
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {loading && <TableRowSkeleton columns={5} />}
          {!loading && invoices.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center" style={{ padding: 32, color: "#9e9e9e" }}>
                Nenhuma fatura encontrada.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Panel: Caixa (new financial control)
// ─────────────────────────────────────────────

const defaultDateFrom = () => moment().startOf("month").format("YYYY-MM-DD");
const defaultDateTo   = () => moment().endOf("month").format("YYYY-MM-DD");

const CaixaPanel = () => {
  const classes = useStyles();

  const [transactions, dispatch] = useReducer(txReducer, []);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo]     = useState(defaultDateTo);
  const [filterType, setFilterType]     = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [dashboard, setDashboard] = useState(null);
  const [modalOpen, setModalOpen]       = useState(false);
  const [selectedTx, setSelectedTx]     = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/transactions/dashboard", {
        params: { dateFrom, dateTo },
      });
      setDashboard(data);
    } catch (err) {
      toastError(err);
    }
  }, [dateFrom, dateTo]);

  const loadTransactions = useCallback(async (page = 1, reset = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions", {
        params: {
          dateFrom,
          dateTo,
          type: filterType || undefined,
          status: filterStatus || undefined,
          pageNumber: page,
        },
      });
      if (reset) dispatch({ type: "RESET" });
      dispatch({ type: "LOAD", payload: data.transactions });
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterType, filterStatus]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    loadDashboard();
    loadTransactions(1, true);
  }, [dateFrom, dateTo, filterType, filterStatus]);

  useEffect(() => {
    if (pageNumber > 1) loadTransactions(pageNumber);
  }, [pageNumber]);

  const { user } = useContext(AuthContext);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });

    socket.on("transaction", (data) => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPDATE", payload: data.transaction });
        loadDashboard();
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE", payload: data.transactionId });
        loadDashboard();
      }
    });

    return () => socket.disconnect();
  }, [user.companyId, loadDashboard]);

  const handleSave = (saved, isUpdate) => {
    dispatch({ type: "UPDATE", payload: saved });
    loadDashboard();
  };

  const handleDelete = async () => {
    const tx = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await api.delete(`/transactions/${tx.id}`);
      dispatch({ type: "DELETE", payload: tx.id });
      loadDashboard();
      toast.success("Lançamento excluído.");
    } catch (err) {
      toastError(err);
    }
  };

  const cards = dashboard
    ? [
        {
          label: "Receitas pagas",
          value: fmtBRL(dashboard.totalIncome),
          icon: <TrendingUpIcon style={{ color: "#2e7d32", fontSize: 24 }} />,
          bg: "#e8f5e9",
        },
        {
          label: "Despesas pagas",
          value: fmtBRL(dashboard.totalExpense),
          icon: <TrendingDownIcon style={{ color: "#c62828", fontSize: 24 }} />,
          bg: "#ffebee",
        },
        {
          label: "Saldo",
          value: fmtBRL(dashboard.balance),
          icon: <AccountBalanceWalletIcon style={{ color: dashboard.balance >= 0 ? "#1565c0" : "#c62828", fontSize: 24 }} />,
          bg: dashboard.balance >= 0 ? "#e3f2fd" : "#ffebee",
          valueColor: dashboard.balance >= 0 ? "#1565c0" : "#c62828",
        },
        {
          label: "A receber",
          value: fmtBRL(dashboard.pendingIncome),
          icon: <AttachMoneyIcon style={{ color: "#f57f17", fontSize: 24 }} />,
          bg: "#fff8e1",
        },
      ]
    : [];

  return (
    <Paper className={classes.mainPaper} variant="outlined">
      <TransactionModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedTx(null); }}
        transaction={selectedTx}
        onSave={handleSave}
      />

      {/* Confirmação de exclusão */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir lançamento?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Tem certeza que deseja excluir o lançamento{" "}
            <strong>"{deleteConfirm?.description}"</strong>?
            Ele será cancelado e sairá da lista, mas permanece no histórico
            para fins de auditoria.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button onClick={handleDelete} variant="contained" style={{ backgroundColor: "#c62828", color: "#fff" }}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dashboard cards */}
      <Grid container spacing={2} className={classes.summaryGrid}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Paper className={classes.summaryCard} elevation={0} variant="outlined">
              <div className={classes.summaryIcon} style={{ backgroundColor: card.bg }}>
                {card.icon}
              </div>
              <div>
                <Typography className={classes.summaryLabel}>{card.label}</Typography>
                <Typography className={classes.summaryValue} style={{ color: card.valueColor }}>
                  {card.value}
                </Typography>
              </div>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Divider style={{ marginBottom: 16 }} />

      {/* Filters */}
      <div className={classes.filterRow}>
        <TextField
          label="De"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={classes.filterField}
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={classes.filterField}
        />
        <FormControl size="small" className={classes.filterField}>
          <InputLabel>Tipo</InputLabel>
          <Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="income">Receita</MenuItem>
            <MenuItem value="expense">Despesa</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" className={classes.filterField}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="pending">Pendente</MenuItem>
            <MenuItem value="paid">Pago</MenuItem>
            <MenuItem value="cancelled">Cancelado</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          className={classes.addButton}
          onClick={() => { setSelectedTx(null); setModalOpen(true); }}
        >
          Nova Transação
        </Button>
      </div>

      {/* Table */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Descrição</TableCell>
            <TableCell>Categoria</TableCell>
            <TableCell align="center">Tipo</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell align="center">Vencimento</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((tx) => {
            const effStatus = getEffectiveTxStatus(tx);
            const stCfg = txStatusConfig[effStatus] || txStatusConfig.pending;
            return (
              <TableRow key={tx.id}>
                <TableCell>
                  <Typography variant="body2">{tx.description}</Typography>
                  {tx.contact && (
                    <Typography variant="caption" color="textSecondary">
                      {tx.contact.name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {tx.category ? (
                    <>
                      <span className={classes.categoryDot} style={{ backgroundColor: tx.category.color }} />
                      {tx.category.name}
                    </>
                  ) : (
                    <span style={{ color: "#bdbdbd" }}>—</span>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={tx.type === "income" ? "Receita" : "Despesa"}
                    size="small"
                    style={{
                      borderRadius: 8,
                      height: 22,
                      fontSize: "0.72rem",
                      backgroundColor: tx.type === "income" ? "#e8f5e9" : "#ffebee",
                      color: tx.type === "income" ? "#2e7d32" : "#c62828",
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <span className={tx.type === "income" ? classes.incomeValue : classes.expenseValue}>
                    {fmtBRL(tx.value)}
                  </span>
                </TableCell>
                <TableCell align="center">{moment(tx.dueDate).format("DD/MM/YYYY")}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={stCfg.label}
                    size="small"
                    style={{ ...stCfg.style, borderRadius: 8, height: 22, fontSize: "0.72rem" }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => { setSelectedTx(tx); setModalOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => setDeleteConfirm(tx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
          {loading && <TableRowSkeleton columns={7} />}
          {!loading && transactions.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" style={{ padding: 32, color: "#9e9e9e" }}>
                Nenhum lançamento encontrado.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Paginação visual */}
      {(hasMore || pageNumber > 1) && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, padding: "16px 0 4px" }}>
          <Button
            size="small"
            variant="outlined"
            disabled={pageNumber === 1 || loading}
            onClick={() => setPageNumber((p) => p - 1)}
          >
            ‹ Anterior
          </Button>
          <Typography variant="body2" style={{ color: "#757575", minWidth: 60, textAlign: "center" }}>
            Página {pageNumber}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={!hasMore || loading}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            Próxima ›
          </Button>
        </div>
      )}
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────

const Financeiro = () => {
  const { licenseExpired } = useContext(AuthContext);
  const location = useLocation();
  // Abre direto em "Faturas" (índice 3) quando: a licença está vencida (o usuário
  // vem para cá para pagar) ou quando a navegação pede explicitamente
  // (?tab=faturas — ex.: botão "Renovar agora" da faixa de aviso).
  const wantsInvoices =
    new URLSearchParams(location.search).get("tab") === "faturas";
  const [tab, setTab] = useState(licenseExpired || wantsInvoices ? 3 : 0);
  const [configOpen, setConfigOpen] = useState(false);

  return (
    <MainContainer>
      <FinanceiroConfigDialog open={configOpen} onClose={() => setConfigOpen(false)} />

      <MainHeader>
        <Title>Financeiro</Title>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginLeft: "auto" }}
        >
          <Tab label="Caixa" />
          <Tab label="A Receber" />
          <Tab label="Relatórios" />
          <Tab label="Faturas" />
        </Tabs>
        <Tooltip title="Configurações financeiras">
          <IconButton onClick={() => setConfigOpen(true)} style={{ marginLeft: 8 }}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </MainHeader>

      {tab === 0 && <CaixaPanel />}
      {tab === 1 && <ContasReceberPanel />}
      {tab === 2 && <RelatorioPanel />}
      {tab === 3 && <InvoicePanel />}
    </MainContainer>
  );
};

export default Financeiro;
